import React, { useState } from "react";
import { careerValues } from "./careersData";

// Expandable value panels (Figma 3811:20838). P is open by default. On a mouse
// the panel opens on hover; keyboard focus opens it too, and click still works
// for touch, where there is no hover. Each panel is a button so Enter and Space
// come for free, and aria-label keeps the accessible name to the value title
// rather than the whole paragraph.
const CareerValues = () => {
    const [openId, setOpenId] = useState(careerValues[0].id);

    return (
        <section id="life-at-pmg" className="w-full bg-primaryBrand scroll-mt-28">
            <div className="container mx-auto px-6 md:px-12 lg:px-20 py-16 lg:py-24 flex flex-col gap-12 lg:gap-16 text-left">
                <h2 className="text-3xl lg:text-5xl font-bold leading-[1.1] text-white">Our values are verbs.</h2>

                <style>{`
                    /* Mobile stacks as an accordion: each card sizes to its own
                       content. flex-grow is only meaningful once the panels share
                       a row, so it is scoped to lg and up — in a column it would
                       govern height and collapse the cards to nothing. */
                    .cv-panel { transition: filter .3s ease; }
                    @media (min-width: 1024px) {
                        .cv-panel {
                            flex-basis: 0;
                            flex-grow: var(--cv-grow, 1);
                            transition: flex-grow .4s ease-in-out, filter .3s ease;
                        }
                    }
                    /* 0fr -> 1fr collapses the row without guessing a max-height,
                       so a collapsed card is letter + title exactly like the
                       design and copy is never clipped mid-line. */
                    .cv-body-row {
                        display: grid;
                        grid-template-rows: 0fr;
                        transition: grid-template-rows .4s ease-in-out;
                    }
                    .cv-body { overflow: hidden; }
                    .cv-body > span {
                        opacity: 0;
                        transform: translateY(12px);
                        transition: opacity .22s ease, transform .22s cubic-bezier(.22,.61,.36,1);
                    }
                    .cv-panel[aria-expanded="true"] .cv-body-row { grid-template-rows: 1fr; }
                    .cv-panel[aria-expanded="true"] .cv-body > span {
                        opacity: 1;
                        transform: none;
                        transition-delay: .2s;
                    }
                    /* collapsing copy fades out first, then the row narrows */
                    .cv-panel[aria-expanded="false"] .cv-body > span { transition-duration: .15s; transition-delay: 0s; }

                    @media (hover: hover) and (pointer: fine) {
                        .cv-panel[aria-expanded="false"]:hover { filter: brightness(1.14); }
                    }
                    .cv-panel[aria-expanded="false"]:focus-visible { filter: brightness(1.14); }
                    .cv-panel:focus-visible { outline: 2px solid #95C3FB; outline-offset: 3px; }

                    /* Immediate width change, opacity only. */
                    @media (prefers-reduced-motion: reduce) {
                        .cv-panel, .cv-body-row { transition: none; }
                        .cv-body > span { transform: none; transition: opacity .15s ease; transition-delay: 0s; }
                        .cv-panel[aria-expanded="true"] .cv-body > span { transform: none; transition-delay: 0s; }
                        .cv-panel[aria-expanded="false"]:hover,
                        .cv-panel[aria-expanded="false"]:focus-visible { filter: none; }
                    }
                `}</style>

                <div className="flex flex-col lg:flex-row gap-5 items-stretch">
                    {careerValues.map((value) => {
                        const isOpen = value.id === openId;
                        const open = () => setOpenId(value.id);
                        return (
                            <button
                                key={value.id}
                                type="button"
                                aria-expanded={isOpen}
                                aria-label={value.title}
                                onMouseEnter={open}
                                onFocus={open}
                                onClick={open}
                                style={{ background: value.background, "--cv-grow": isOpen ? 660 : 326 }}
                                className="cv-panel text-left lg:h-[390px] flex flex-col justify-between gap-6 overflow-hidden rounded-3xl border border-solid border-[rgba(255,255,255,0.1)] p-6 lg:p-8 shadow-[0px_12px_36px_0px_rgba(15,20,36,0.05)]"
                            >
                                <span
                                    aria-hidden="true"
                                    className="block text-[72px] lg:text-[112px] font-bold leading-[0.82]"
                                    style={{ color: value.letterColor }}
                                >
                                    {value.letter}
                                </span>

                                <span className="block w-full">
                                    <span
                                        className="block text-xl lg:text-2xl font-bold leading-[1.2]"
                                        style={{ color: value.titleColor }}
                                    >
                                        {value.title}
                                    </span>
                                    <span className="cv-body-row block">
                                        <span className="cv-body block">
                                            <span
                                                className="block mt-3 text-sm lg:text-base leading-[1.5]"
                                                style={{ color: value.bodyColor }}
                                                aria-hidden={!isOpen}
                                            >
                                                {value.description}
                                            </span>
                                        </span>
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default CareerValues;
